import Grid from "antd/lib/grid";
import Typography from "antd/lib/typography";
import clsx from "clsx";
import { useState, useEffect, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CompetencesLegend } from "src/features/dashboards/components";

import { IClassroom, IFrameworkResource, useClassroomLearnersProgressionQuery } from "@sol/sdk";
import {
    IClassroomLearnersProgression,
    ISkillWithLevels,
} from "@sol/sdk/classrooms/types/IClassroomLearnersProgression";
import { Empty } from "@sol/uikit/data-display/Empty/Empty";
import { Table, TableColumnType } from "@sol/uikit/data-display/Table";
import { Select } from "@sol/uikit/data-entry/Select/Select";
import { Skeleton } from "@sol/uikit/feedback/Skeleton/Skeleton";

import { SkillProgressBadge } from "@/entities/skill";

const rankFromCode = (code: string): number => {
    const match = code.match(/(\d+)$/);
    return match ? Number(match[1]) : 0;
};

export const prepareTableData = (
    framework: IFrameworkResource & { skills: ISkillWithLevels[] },
    learners: IClassroomLearnersProgression[],
) => {
    return framework.skills
        .sort((a: ISkillWithLevels, b: ISkillWithLevels) => {
            const numA = rankFromCode(a.code);
            const numB = rankFromCode(b.code);
            return numA > numB ? 1 : -1;
        })
        .map((skill: ISkillWithLevels) => {
            const row: any = {
                key: skill["@id"],
                skillCode: skill.code,
                skillTitle: `${skill.code}. ${skill.shortTitle}`,
            };

            learners.forEach(learner => {
                const learnerFramework = learner.frameworks.find(f =>
                    f.skills.some((s: ISkillWithLevels) => s["@id"] === skill["@id"]),
                );
                const learnerSkill = learnerFramework?.skills.find((s: ISkillWithLevels) => s["@id"] === skill["@id"]);

                row[learner["@id"]] = !learnerSkill
                    ? null
                    : [
                          { level: 1, status: learnerSkill.skillLevel1 },
                          { level: 2, status: learnerSkill.skillLevel2 },
                          { level: 3, status: learnerSkill.skillLevel3 },
                      ];
            });

            return row;
        });
};

const prepareTableColumns = (
    learners: IClassroomLearnersProgression[],
    skillLabel: string | React.ReactNode,
    isMobile: boolean,
): TableColumnType[] => {
    return [
        {
            title: skillLabel,
            dataIndex: "skillTitle",
            key: "skill",
            fixed: isMobile ? false : "left",
            width: isMobile ? 240 : undefined,
            minWidth: isMobile ? 120 : 240,
            align: isMobile ? "center" : "left",
            render: (text: string) => (
                <Typography.Text title={text} className="line-clamp-1 overflow-hidden  font-ant-strong break-words">
                    {text}
                </Typography.Text>
            ),
        },
        ...learners.map((learner: IClassroomLearnersProgression) => ({
            title: (
                <span className="block w-[80px]">
                    <span className="block truncate font-medium capitalize" title={learner.name}>
                        {learner.name?.toLowerCase()}
                    </span>
                    <span className="block truncate capitalize" title={learner.surname}>
                        {learner.surname?.toLowerCase()}
                    </span>
                </span>
            ),
            dataIndex: learner["@id"],
            key: learner["@id"],
            fixed: false,
            width: 80,
            align: "center" as const,
            render: (cellData: { status: "acquired" | "assigned" | "unassigned"; level: number }[] | null) => (
                <div className="flex w-[80px] justify-center">
                    <SkillProgressBadge profile={cellData} />
                </div>
            ),
        })),
    ];
};

export const CompetencesDashboard = ({ classroom, className }: { classroom?: IClassroom; className?: string }) => {
    const [t] = useTranslation();

    const { data: learnersData, isLoading } = useClassroomLearnersProgressionQuery({ classroom });

    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const learners = learnersData?.["hydra:member"].flat() || [];
    const frameworks = learners[0]?.frameworks.toReversed() || [];
    const [selectedFramework, setSelectedFramework] = useState("");

    useEffect(() => {
        if (frameworks.length > 0) {
            const firstFrameworkId = frameworks[0]?.["@id"] || "";
            if (!selectedFramework || !frameworks.some(f => f["@id"] === selectedFramework)) {
                setSelectedFramework(firstFrameworkId);
            }
        }
    }, [frameworks, selectedFramework]);

    const handleFrameworkChange = (key: string) => {
        setSelectedFramework(key);
    };

    let children: ReactNode = null;

    if (isLoading) {
        children = <Skeleton.Node className="size-full!" active />;
    } else if (learners.length < 1 || frameworks.length < 1) {
        children = (
            <div className="flex size-full flex-col items-center justify-center">
                <Empty
                    description={
                        <div className="text-center text-color-ant-tertiary">
                            {t("features.dashboards.details.skillsProgress.noData")}
                        </div>
                    }
                />
            </div>
        );
    } else {
        children = frameworks
            .filter(framework => framework["@id"] === selectedFramework)
            .map(framework => {
                const tableData = prepareTableData(framework, learners);
                const tableColumns = prepareTableColumns(
                    learners,
                    <Select
                        prefix={isMobile ? undefined : t("features.dashboards.details.skillsProgress.skill")}
                        value={selectedFramework}
                        onChange={handleFrameworkChange}
                        options={frameworks.map(framework => ({
                            label: framework.title,
                            value: framework["@id"],
                        }))}
                        className={isMobile ? "max-w-[180px]" : "max-w-full"}
                    />,
                    isMobile,
                );

                return (
                    <Table
                        className="size-full"
                        tableLayout="auto"
                        key={framework["@id"]}
                        columns={tableColumns}
                        dataSource={tableData}
                        loading={isLoading}
                        scroll={{ y: "100%" }}
                        pagination={false}
                        size="small"
                        bordered
                    />
                );
            });
    }

    return (
        <div className={clsx("flex flex-col items-start gap-ant-md", className)}>
            <CompetencesLegend />

            <div className="min-h-0 w-full flex-1 overflow-hidden">{children}</div>
        </div>
    );
};

export default CompetencesDashboard;
